import { useSelector, shallowEqual } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useMeterType } from './useMeterType';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';
import { selectElectricVehicle } from 'state/electric-vehicle/electric-vehicle-slice';
import { usePartner } from 'components/app/routes/usePartner';
import { usePaymentMethod } from 'hooks/usePaymentMethod';
import { useEvTariffInformation } from './useEvTariffInformation';

export const useIsEligiblePaymentMethod = () => {
  const { acceptedPaymentMethods } = usePartner();
  const { paymentMethod } = usePaymentMethod();

  return acceptedPaymentMethods.includes(paymentMethod);
};

export const useEligibilityCriteria = () => {
  const { showEvTariffInformation } = useEvTariffInformation();
  const { configTimeOfUseTariff } = useFlags();
  const fuelType = useSelector(selectFuelType, shallowEqual);
  const choseElectricVehicle = useSelector(selectElectricVehicle, shallowEqual);
  const {
    isUneligibleElectricityMeter,
    isUneligibleGasMeter,
    hasElectricityTOUMeter
  } = useMeterType();
  const isEligiblePaymentMethod = useIsEligiblePaymentMethod();

  const { gas, electric } = fuelType;
  const gasOnly = gas && !electric;
  const electricityOnly = !gas && electric;
  const isElectricVehicleEligible =
    showEvTariffInformation ||
    (choseElectricVehicle
      ? !!(configTimeOfUseTariff && hasElectricityTOUMeter)
      : true);

  return {
    gasOnly,
    isUneligibleGasMeter,
    isEligiblePaymentMethod,
    electricityOnly,
    isUneligibleElectricityMeter,
    isElectricVehicleEligible
  };
};

export const useIsEligibleCustomer = () => {
  const {
    gasOnly,
    isUneligibleGasMeter,
    isEligiblePaymentMethod,
    electricityOnly,
    isUneligibleElectricityMeter,
    isElectricVehicleEligible
  } = useEligibilityCriteria();

  const isGasEligible = !isUneligibleGasMeter && isEligiblePaymentMethod;
  const isElecEligible =
    !isUneligibleElectricityMeter &&
    isElectricVehicleEligible &&
    isEligiblePaymentMethod;
  const isDualFuelEligible = isGasEligible && isElecEligible;

  switch (true) {
    case gasOnly:
      return isGasEligible;
    case electricityOnly:
      return isElecEligible;
    default:
      return isDualFuelEligible;
  }
};
